import { AlertTriangle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from '@/components/ui/dialog';

/**
 * Super-admin destructive-action confirm modal.
 *
 * Only shown to super-admins for guarded destructive ops on production data
 * (invoice status downgrades and payment voids — see
 * src/lib/superAdminDestructiveGuard.ts). On confirm, the caller sends the
 * X-Super-Admin-Confirm-Destructive header carrying the invoice id so the
 * backend guard (utils/super_admin_destructive_guard.py) lets the op through
 * instead of 403ing. Non-super-admins never see this dialog.
 *
 * This HONORS the backend guard — it is the deliberate per-entity confirm the
 * guard requires; it does not bypass it.
 */
export interface SuperAdminDestructiveConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Short op description, e.g. "downgrade Sent → Draft" or "void payment". */
  opDescription: string;
  /** Entity label the op targets, e.g. "invoice #0042". */
  entityLabel: string;
  /** Confirm button label. Defaults to "Confirm destructive action". */
  confirmLabel?: string;
  /** Whether the underlying mutation is in flight (disables confirm). */
  isPending?: boolean;
  onConfirm: () => void;
}

export function SuperAdminDestructiveConfirmDialog({
  open,
  onOpenChange,
  opDescription,
  entityLabel,
  confirmLabel = 'Confirm destructive action',
  isPending = false,
  onConfirm,
}: SuperAdminDestructiveConfirmDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="super-admin-destructive-confirm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700">
            <AlertTriangle className="h-5 w-5 shrink-0" />
            Super-admin destructive action
          </DialogTitle>
          <DialogDescription className="pt-2 text-sm text-gray-700">
            You are a super-admin acting on <span className="font-medium">production data</span>.
            Confirm <span className="font-medium">{opDescription}</span> on{' '}
            <span className="font-medium">{entityLabel}</span>.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2.5 text-sm text-red-800">
          This action is normally blocked for super-admins to prevent bulk-loop
          accidents. Confirming sends a one-time per-invoice override for this
          single action only.
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <button
              type="button"
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              data-testid="super-admin-destructive-cancel"
            >
              Cancel
            </button>
          </DialogClose>
          <button
            type="button"
            disabled={isPending}
            onClick={onConfirm}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="super-admin-destructive-confirm-btn"
          >
            {isPending ? 'Working…' : confirmLabel}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
